import { useMemo, useState } from 'react';
import {
  DEFAULT_CHAOS_ROWS,
  MAX_CHAOS_ROWS,
  generateChaos,
} from '../lib/fuzzer';
import { baseFileName, chaosToCsv, downloadFile, toJson } from '../lib/exporters';
import type { ColumnSchema, ChaosRow } from '../lib/types';

interface FuzzerPanelProps {
  columns: ColumnSchema[];
  isPro: boolean;
  sourceName: string;
  onUpgrade: () => void;
}

const PREVIEW_ROWS = 12;

interface ChaosStats {
  sql: number;
  xss: number;
  unicode: number;
  nullLike: number;
  boundaries: number;
  nulls: number;
}

function collectStats(rows: ChaosRow[]): ChaosStats {
  const stats: ChaosStats = { sql: 0, xss: 0, unicode: 0, nullLike: 0, boundaries: 0, nulls: 0 };
  for (const row of rows) {
    for (const value of Object.values(row)) {
      if (value === null) {
        stats.nulls++;
        continue;
      }
      if (/--|;\s*(DROP|UPDATE|DELETE|INSERT)|'\s*OR|UNION SELECT|WAITFOR/i.test(value)) stats.sql++;
      if (/<script|onerror\s*=|onload\s*=|javascript:/i.test(value)) stats.xss++;
      if (/[\u{1F300}-\u{1FAFF}\u{0590}-\u{05FF}\u{0600}-\u{06FF}]|[\u200B-\u200D]/u.test(value)) stats.unicode++;
      if (/^(null|undefined|NaN|nil|None|\(null\)|\\N)$/i.test(value.trim())) stats.nullLike++;
      if (value.length >= 255 || value.length === 256) stats.boundaries++;
    }
  }
  return stats;
}

export function FuzzerPanel({ columns, isPro, sourceName, onUpgrade }: FuzzerPanelProps) {
  const [rows, setRows] = useState(DEFAULT_CHAOS_ROWS);
  const [seed, setSeed] = useState(1337);
  const [chaos, setChaos] = useState<ChaosRow[] | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const headers = useMemo(() => columns.map((column) => column.name), [columns]);
  const stats = useMemo(() => (chaos ? collectStats(chaos) : null), [chaos]);

  const generate = () => {
    setExportError(null);
    setChaos(generateChaos(columns, { rows, seed }));
  };

  const handleExport = (format: 'csv' | 'json') => {
    if (!chaos) return;
    if (!isPro) {
      setExportError('Chaos dataset exports are a Pro feature.');
      onUpgrade();
      return;
    }
    const base = baseFileName(sourceName, 'dataset');
    if (format === 'csv') {
      downloadFile(`${base}_chaos_${rows}.csv`, chaosToCsv(headers, chaos), 'text/csv');
    } else {
      downloadFile(`${base}_chaos_${rows}.json`, toJson(chaos), 'application/json');
    }
  };

  return (
    <section className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-4 shadow-panel sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-200">
            <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor">
              <path d="M11.3 1.5 3 11.5h5l-1.3 7L17 8.5h-5l1.3-7Z" />
            </svg>
            The “Break-My-App” fuzzer
          </h2>
          <p className="mt-1 max-w-2xl text-xs text-slate-400">
            Synthesizes {rows} adversarial-but-schema-plausible rows: SQL injection, XSS, ZWJ emoji &amp; RTL
            strings, null-like strings, 255-char boundaries, float precision traps and epoch-boundary dates.
            Deterministic per seed — a failing row number is always reproducible.
          </p>
        </div>
        <span className="rounded-full bg-slate-800/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          {isPro ? 'export unlocked' : 'export requires Pro'}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium text-slate-400">Rows</span>
          <input
            type="number"
            min={1}
            max={MAX_CHAOS_ROWS}
            value={rows}
            onChange={(event) => setRows(Math.max(1, Math.min(MAX_CHAOS_ROWS, Number(event.target.value) || 1)))}
            className="w-24 rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-1.5 font-mono text-sm text-slate-100 outline-none focus:border-amber-500"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] font-medium text-slate-400">Seed</span>
          <input
            type="number"
            value={seed}
            onChange={(event) => setSeed(Number(event.target.value) || 0)}
            className="w-32 rounded-lg border border-slate-700 bg-slate-950 px-2.5 py-1.5 font-mono text-sm text-slate-100 outline-none focus:border-amber-500"
          />
        </label>
        <button
          type="button"
          onClick={generate}
          disabled={columns.length === 0}
          className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-amber-400 disabled:opacity-40"
        >
          Generate Chaos Dataset
        </button>
        <button
          type="button"
          onClick={() => setSeed(Math.floor(Math.random() * 100_000))}
          className="rounded-lg border border-slate-700 px-3 py-2 text-xs font-medium text-slate-300 transition hover:border-slate-500 hover:text-white"
        >
          Random seed
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleExport('csv')}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition ${
              isPro
                ? 'bg-slate-100 text-slate-900 hover:bg-white'
                : 'border border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white'
            }`}
          >
            {!isPro && <Lock />} Export .csv
          </button>
          <button
            type="button"
            onClick={() => handleExport('json')}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition ${
              isPro
                ? 'bg-slate-100 text-slate-900 hover:bg-white'
                : 'border border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white'
            }`}
          >
            {!isPro && <Lock />} Export .json
          </button>
        </div>
      </div>

      {exportError && (
        <p className="mt-3 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200 ring-1 ring-amber-500/25">
          {exportError}
        </p>
      )}

      {chaos && stats && (
        <>
          <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
            <Stat label="rows" value={chaos.length} />
            <Stat label="SQLi payloads" value={stats.sql} tone="rose" />
            <Stat label="XSS payloads" value={stats.xss} tone="rose" />
            <Stat label="unicode / RTL / ZWJ" value={stats.unicode} tone="amber" />
            <Stat label="null-like strings" value={stats.nullLike} tone="sky" />
            <Stat label="boundary lengths" value={stats.boundaries} tone="amber" />
            <Stat label="real nulls" value={stats.nulls} tone="slate" />
          </div>

          <div className="scrollbar-thin mt-3 max-h-72 overflow-auto rounded-xl border border-slate-800 bg-slate-950">
            <table className="w-full border-collapse text-left font-mono text-[11px]">
              <thead className="sticky top-0 bg-slate-900">
                <tr>
                  <th className="w-10 border-b border-slate-800 px-2 py-1.5 text-[10px] uppercase text-slate-500">#</th>
                  {headers.map((header) => (
                    <th key={header} className="border-b border-l border-slate-800 px-2 py-1.5 text-slate-300">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chaos.slice(0, PREVIEW_ROWS).map((row, index) => (
                  <tr key={index} className="even:bg-slate-900/40">
                    <td className="border-b border-slate-800/60 px-2 py-1 text-slate-600">{index + 1}</td>
                    {headers.map((header) => {
                      const value = row[header] ?? null;
                      return (
                        <td
                          key={header}
                          className="max-w-[200px] truncate border-b border-l border-slate-800/60 px-2 py-1 text-slate-300"
                          title={value ?? 'null'}
                        >
                          {value === null ? (
                            <span className="text-slate-600 italic">null</span>
                          ) : (
                            value.length > 60 ? `${value.slice(0, 59)}…` : value
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">
            Preview shows the first {Math.min(PREVIEW_ROWS, chaos.length)} of {chaos.length} rows.
          </p>
        </>
      )}
    </section>
  );
}

function Lock() {
  return (
    <svg viewBox="0 0 20 20" className="h-3.5 w-3.5 text-amber-400" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M10 1a4 4 0 0 0-4 4v2H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-1V5a4 4 0 0 0-4-4Zm2 6H8V5a2 2 0 1 1 4 0v2Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function Stat({ label, value, tone = 'slate' }: { label: string; value: number; tone?: string }) {
  const tones: Record<string, string> = {
    rose: 'bg-rose-500/10 text-rose-300 ring-rose-500/25',
    amber: 'bg-amber-500/10 text-amber-300 ring-amber-500/25',
    sky: 'bg-sky-500/10 text-sky-300 ring-sky-500/25',
    slate: 'bg-slate-800 text-slate-400 ring-slate-700',
  };
  return (
    <span className={`rounded-full px-2.5 py-1 font-semibold ring-1 ${tones[tone] ?? tones.slate}`}>
      {value.toLocaleString()} {label}
    </span>
  );
}
